import { useMutation } from "@tanstack/react-query";
import { t } from "i18next";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import {
  authWithApple,
  authWithGoogle,
  getUserStatus,
  signInUser,
  signUpUser,
} from "../api/auth/authApi";
import { completeOAuthRedirect, type OAuthProvider } from "../firebase/completeOAuthRedirect";
import { reportOAuthError } from "../firebase/oauthErrors";
import { useAuthStore } from "../store/useAuthStore";

// ─── Where to go after signing in ───────────────────────────────────────
// RequireAuth passes the blocked page as `state.from` (a Location); links may
// pass a plain path. Google/Apple on iOS leave the page for a full redirect,
// so the target is also kept in sessionStorage until the round trip ends.
const RETURN_KEY = "auth_return_to";
const DEFAULT_AFTER_AUTH = "/listings";

type FromState = { from?: string | { pathname?: string; search?: string; hash?: string } } | null;

export const returnPathFrom = (state: unknown): string => {
  const from = (state as FromState)?.from;
  if (typeof from === "string") return from;
  if (from?.pathname) return `${from.pathname}${from.search || ""}${from.hash || ""}`;
  return DEFAULT_AFTER_AUTH;
};

export const rememberReturnPath = (path: string) => {
  try {
    sessionStorage.setItem(RETURN_KEY, path);
  } catch {
    /* private mode — falls back to the default page */
  }
};

const takeReturnPath = (fallback: string): string => {
  try {
    const p = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    // Only same-site paths — never an absolute URL from storage.
    if (p && p.startsWith("/") && !p.startsWith("//")) return p;
  } catch {
    /* ignore */
  }
  return fallback;
};

export type AuthMethod = "email" | "google" | "apple" | "link" | "microsoft" | "facebook";
const LAST_METHOD_KEY = "auth:lastMethod";

/** How the user signed in last time on this device (for the "last used" hint). */
export const readLastAuthMethod = (): AuthMethod | null => {
  try {
    return (localStorage.getItem(LAST_METHOD_KEY) as AuthMethod | null) || null;
  } catch {
    return null;
  }
};

/** Stores tokens, loads the profile (so the header shows the name at once) and navigates on. */
export const useFinishSignIn = () => {
  const setTokens = useAuthStore((s) => s.setTokens);
  const setUserData = useAuthStore((s) => s.setUserData);
  const navigate = useNavigate();
  const location = useLocation();
  return async (
    data: { access_token: string; refresh_token: string },
    opts: { method?: AuthMethod; stay?: boolean } = {},
  ) => {
    setTokens(data.access_token, data.refresh_token);
    if (opts.method) {
      try {
        localStorage.setItem(LAST_METHOD_KEY, opts.method);
      } catch {
        /* ignore */
      }
    }
    // Go on at once; the profile (name/avatar in the header) arrives a moment later.
    // One Tap signs in on the page the user is reading — no navigation then.
    if (!opts.stay) navigate(takeReturnPath(returnPathFrom(location.state)), { replace: true });
    getUserStatus()
      .then(setUserData)
      .catch(() => {
        /* non-fatal: App re-checks status on the next load */
      });
  };
};

export const useLogin = () => {
  const finish = useFinishSignIn();
  return useMutation({ mutationFn: signInUser, onSuccess: (d) => finish(d, { method: "email" }) });
};

export const useRegister = () => {
  const finish = useFinishSignIn();
  return useMutation({
    mutationFn: signUpUser,
    onSuccess: async (data) => {
      await finish(data, { method: "email" });
      toast.success(t("register.success", "Регистрация прошла успешно!"));
    },
  });
};

export const useAuth = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated;
};

export const useAuthWithGoogle = () => {
  const finish = useFinishSignIn();
  return useMutation({
    mutationFn: (accessToken: string) => authWithGoogle(accessToken),
    onSuccess: (d) => finish(d, { method: "google" }),
    onError: () => {
      toast.error(t("auth.googleError", "Не удалось войти через Google. Попробуйте снова."));
    },
  });
};

export const useAuthWithApple = () => {
  const finish = useFinishSignIn();
  return useMutation({
    mutationFn: (accessToken: string) => authWithApple(accessToken),
    onSuccess: (d) => finish(d, { method: "apple" }),
    onError: () => {
      toast.error(t("auth.appleError", "Не удалось войти через Apple. Попробуйте снова."));
    },
  });
};

/**
 * Any other Firebase provider (Microsoft, Facebook, One Tap, email link): the
 * backend's Google endpoint accepts every Firebase ID token that carries an email.
 */
export const useAuthWithFirebase = (method: AuthMethod, opts: { stay?: boolean } = {}) => {
  const finish = useFinishSignIn();
  return useMutation({
    mutationFn: (idToken: string) => authWithGoogle(idToken),
    onSuccess: (d) => finish(d, { method, stay: opts.stay }),
    onError: () => {
      toast.error(t("authFlow.errors.unknown", "Что-то пошло не так. Попробуйте ещё раз."));
    },
  });
};

/**
 * Finishes a redirect-based Google/Apple sign-in once the browser returns to
 * the app. On web the OAuth flow is a full-page redirect (popups are blocked
 * on iOS Safari), so the token is only available on the next load — here we
 * pick it up and run the same backend auth as the button handlers.
 */
export const useCompleteOAuthRedirect = () => {
  const googleAuthMutation = useAuthWithGoogle();
  const appleAuthMutation = useAuthWithApple();
  const microsoftMutation = useAuthWithFirebase("microsoft");
  const facebookMutation = useAuthWithFirebase("facebook");
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    completeOAuthRedirect()
      .then((result) => {
        if (!result) return;
        const m = {
          google: googleAuthMutation,
          apple: appleAuthMutation,
          microsoft: microsoftMutation,
          facebook: facebookMutation,
        }[result.provider];
        m.mutate(result.idToken);
      })
      .catch((error) => {
        const provider = (error as { provider?: OAuthProvider })?.provider;
        if (provider) reportOAuthError(provider, error);
        else console.warn("OAuth redirect sign-in did not complete:", error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const useUserStatus = () => {
  const setUserData = useAuthStore((s) => s.setUserData);
  return useMutation({
    mutationFn: getUserStatus,
    onSuccess: (data) => {
      setUserData(data);
    },
  });
};
